import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";

// Assets
import IconCheckMark from "../../../Assets/SVGs/Icons/YesOrNo/CheckMark";

// Models
import PlanDTO from "../../../Models/DTOs/Plan";
import {SubscriptionDTO} from "../../../Models/DTOs/Subscription";

// Hooks
import useFetchSubscriptionPlans from "../../../Hooks/Plans/useFetchSubscriptionPlans";
import SignupAffiliatePlanPrice, {SignupAffiliatePricing} from "./SignupAffiliatePlanPrice";
import {IsSubscriptionActive} from "../../../Helpers/Subscription";
import {formatMoney} from "../../../Helpers/Currency/Currency";
import {
    getOfferPriceQualifier,
    getOfferRenewalText,
    hasSpecialOfferStyling,
    isPrepaidOffer,
    getOfferPercentOff,
    getResolvedOffer,
    PromotionMode,
} from "../../../Helpers/BillingOffers";

interface PlansSelectorProps {
    onSelectPlan: (plan: PlanDTO) => void;
    parentSelectedPlan?: PlanDTO | null;
    hasError: boolean;
    isDarkMode: boolean;
    errorMessage: string;
    featuresStart: number;
    featuresEnd: number;
    subscriptionData?: SubscriptionDTO | null;
    passPlanDataToParent?: (plans: PlanDTO[]) => void,
    priceOverrides?: PriceOverrides[];
    promotionMode?: PromotionMode;
    signupAffiliatePricing?: SignupAffiliatePricing;
}

export interface PriceOverrides {
    planKey: string,
    planPrice: number
}

const PlansSelector: React.FC<PlansSelectorProps> = (
    {
        onSelectPlan,
        parentSelectedPlan,
        hasError,
        isDarkMode,
        errorMessage,
        featuresStart,
        featuresEnd,
        subscriptionData,
        passPlanDataToParent,
        priceOverrides,
        promotionMode = "acquisition",
        signupAffiliatePricing,
    }
) => {
    const
        { plans } = useFetchSubscriptionPlans({promotionMode}),
        [selectedPlan, setSelectedPlan] = useState<PlanDTO | null>(null),

        { t } = useTranslation(),

        upgradeMode = IsSubscriptionActive(subscriptionData?.Status) &&
            (subscriptionData?.BillingIntervalCount ?? 1) === 1 &&
            (subscriptionData?.BillingInterval?.toLowerCase() === "month" || subscriptionData?.PlanId === 1),

        displayPrice = (plan: PlanDTO) => {
            const match = priceOverrides && priceOverrides.find(o => o.planKey === plan.key);
            if (match) return formatMoney(match.planPrice,  plan.currency);
            const offer = getResolvedOffer(plan);
            if (offer) return formatMoney(offer.final_amount_minor, offer.currency);

            // These legacy fields remain a display fallback while the offer
            // catalog is rolled out. They are acquisition-only and are never
            // shown to a cancelled member or on the monthly side of an upgrade.
            if (promotionMode === "acquisition" && !plan.offerCatalogResolved) {
                if (plan.promotionIsActive && plan.promotionPrice != null) {
                    return formatMoney(plan.promotionPrice, plan.currency);
                }
                if (plan.standardDiscount?.stdPromoPrice != null) {
                    return formatMoney(plan.standardDiscount.stdPromoPrice, plan.currency);
                }
            }

            return formatMoney(plan.price, plan.currency);
        },

        handleSelectPlan = (plan: PlanDTO) => {
            setSelectedPlan(plan);
            onSelectPlan(plan);
        };

    // If a parent selected plan is provided, sync it with local state
    useEffect(() => {
        if (parentSelectedPlan) {
            setSelectedPlan(parentSelectedPlan);
        }

        if (passPlanDataToParent && plans) {
            passPlanDataToParent(plans);
        }
    }, [parentSelectedPlan, plans]);

    // Once plans are fetched, check if a plan is specified in the query string
    useEffect(() => {
        if (plans && plans.length > 0) {
            const searchParams = new URLSearchParams(window.location.search);
            const planParam = searchParams.get('plan');

            // An active monthly membership has exactly one supported transition:
            // monthly -> annual. Never let an acquisition link preselect the
            // member's current monthly plan in this context.
            if (upgradeMode) {
                const annualPlan = plans.find((plan) => plan.period === 1);
                if (annualPlan) {
                    setSelectedPlan(annualPlan);
                    onSelectPlan(annualPlan);
                }

                if (planParam) {
                    searchParams.delete('plan');
                    const query = searchParams.toString();
                    window.history.replaceState(
                        {},
                        '',
                        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
                    );
                }
            }
            else if (planParam) {
                // Convert hyphen-separated string into a space-separated string
                // e.g. "Truth-Seeker" -> "Truth Seeker"
                const planTitleFromUrl = planParam.replace(/-/g, ' ');

                const matchedPlan = plans.find(
                    p => p.title.toLowerCase() === planTitleFromUrl.toLowerCase()
                );

                if (matchedPlan) {
                    setSelectedPlan(matchedPlan);
                    onSelectPlan(matchedPlan);

                    // Now remove `plan` from query string
                    searchParams.delete('plan');
                    const query = searchParams.toString();
                    window.history.replaceState(
                        {},
                        '',
                        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
                    );
                }
            }
        }
    }, [plans, onSelectPlan, upgradeMode]);

    return (
        <div
            className={`layout--plan-selector ${!plans || plans.length === 0 ? 'is-loading' : 'is-not-loading'} ${isDarkMode ? 'is-dark-mode' : 'is-light-mode'}`}
        >
            {plans && plans.length > 0 ? (
                plans
                    .map((plan, i) => {
                        const offer = getResolvedOffer(plan);
                        const offerQualifier = getOfferPriceQualifier(offer);
                        const specialOffer = hasSpecialOfferStyling(offer);
                        const saleClass = specialOffer ? ` membership-sale membership-sale--${plan.period === 1 ? "annual" : "monthly"}` : "";

                        const standardPrice = (
                            <h3>
                                <>
                                    <span className="plan-selector__highlighted-price">{displayPrice(plan)}</span>{' '}

                                    {!isPrepaidOffer(offer) && offer?.duration === "repeating" ? `per ${offer.cadence} ` : ""}{offer ? offerQualifier : t(`${plan.key}-label-billed`)}

                                    {getOfferRenewalText(offer) && (
                                        <span className="plan-selector__small-price">
                                            {getOfferRenewalText(offer)}
                                        </span>
                                    )}
                                </>
                            </h3>
                        );

                        return (
                        <div
                            key={i}
                            className={`form__plan-single${saleClass} ${
                                selectedPlan !== null ? 'plan-selected' : 'no-plan-selected'
                            } ${
                                selectedPlan?.planId === plan.planId ? 'is-selected' : 'is-not-selected'
                            } ${subscriptionData && IsSubscriptionActive(subscriptionData.Status) && subscriptionData.PlanId === plan.planId ? 'has-current-plan' : 'does-not-have-this-plan' }`}
                            onClick={upgradeMode ? () => {} : () => handleSelectPlan(plan)}
                        >
                            {/** isSelected YesOrNo **/}
                            {selectedPlan?.planId === plan.planId && <IconCheckMark/>}

                            {/** Plan name / label **/}
                            <p className="plan-single__label u-relative_hidden">
                                {subscriptionData && IsSubscriptionActive(subscriptionData?.Status) && subscriptionData.PlanId === plan.planId
                                    ? "Current Plan"
                                    : offer?.headline || plan.title}
                            </p>

                            {specialOffer && <span className="membership-sale__badge">{offer?.badge || "Special offer"}{getOfferPercentOff(offer) ? ` · Save ${getOfferPercentOff(offer)}%` : ""}</span>}

                            {/** Billing Period **/}
                            {(!subscriptionData || upgradeMode) && (
                                <h2>{t(`${plan.key}-label`)}</h2>
                            )}

                            {/** Price Information **/}
                            {signupAffiliatePricing ? (
                                <SignupAffiliatePlanPrice plan={plan} pricing={signupAffiliatePricing}>{standardPrice}</SignupAffiliatePlanPrice>
                            ) : standardPrice}

                            {offer?.description && (
                                <p className="plan-single__offer-description">{offer.description}</p>
                            )}

                            {offer?.terms && (
                                <p className="plan-single__offer-terms">{offer.terms}</p>
                            )}

                            {/** Plan features **/}
                            {plan.features && plan.features.length > 0 && (
                                <ul>
                                    {plan.features.map((feature, j) => (
                                        <li className="plan-single__feature" key={i + j}>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {i !== 0 && !specialOffer && (
                                <div className="plan-single__flag u-full_cover_absolute">
                                    <span className="u-full_cover_absolute">Best Value</span>
                                </div>
                            )}
                        </div>
                        );
                    })
            ) : (
                <>
                    {/** Loading UI **/}
                    <div className="form__plan-single u-relative_hidden">
                        <div
                            className={`u-full_cover_absolute loader--side-to-side ${
                                isDarkMode ? 'is-dark-mode' : 'is-light-mode'
                            }`}
                        />
                    </div>

                    <div className="form__plan-single u-relative_hidden">
                        <div
                            className={`u-full_cover_absolute loader--side-to-side ${
                                isDarkMode ? 'is-dark-mode' : 'is-light-mode'
                            }`}
                        />
                    </div>
                </>
            )}

            {hasError && <label className="error-message">{errorMessage}</label>}
        </div>
    );
};

export default PlansSelector;
